import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, User, Eye, EyeOff, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";
import { Field } from "@/pages/Setup";

export default function Invite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { startSession } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This invitation link is incomplete.");
      return;
    }
    api.get(`/invite/${encodeURIComponent(token)}`).then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const f = new FormData(e.currentTarget);
    const username = String(f.get("username") || "").trim();
    const password = String(f.get("password") || "");
    if (username.length < 3) return setError("Username must be at least 3 characters.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== String(f.get("confirm") || "")) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/invite/accept", { token, username, password });
      startSession(data);
      navigate("/creator/profile?welcome=1", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const eye = (
    <button type="button" onClick={() => setShow(!show)} aria-label="Toggle password" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a7a0af]">
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <div data-testid="invite-screen" className="relative min-h-screen bg-[#080808] grain">
      <div className="absolute -top-16 -left-10 h-56 w-56 glow-magenta opacity-40" />
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-10 pt-12">
        <div className="flex flex-col items-center">
          <PeepingLogo className="w-48" />
          <span className="mt-3 flex items-center gap-1.5 rounded-full border border-[#d42be8]/40 bg-[#d42be8]/10 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-[#f0a8fa]">
            <Sparkles size={12} /> Creator Invitation
          </span>
        </div>
        {info ? (
          <>
            <div className="mt-10">
              <h1 data-testid="invite-title" className="text-2xl font-bold">Welcome, {info.display_name}</h1>
              <p className="mt-1 text-sm text-[#a7a0af]">Set up your creator account for <span className="text-white">{info.email}</span> (@{info.handle}).</p>
            </div>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-3" noValidate>
              <Field id="invite-username" icon={User} label="Username" name="username" data-testid="invite-username" placeholder="Choose a username" autoComplete="nickname" />
              <Field id="invite-password" icon={Lock} label="Password" name="password" type={show ? "text" : "password"} data-testid="invite-password" placeholder="At least 8 characters" autoComplete="new-password" right={eye} />
              <Field id="invite-confirm" icon={Lock} label="Confirm password" name="confirm" type={show ? "text" : "password"} data-testid="invite-confirm" placeholder="Re-enter your password" autoComplete="new-password" />
              {error && <p data-testid="invite-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
              <button type="submit" disabled={loading} data-testid="invite-submit" className="btn-brand mt-2 w-full rounded-2xl py-4 text-base font-semibold text-white">
                {loading ? "Creating account…" : "Create creator account"}
              </button>
            </form>
          </>
        ) : (
          <p data-testid="invite-error" className="mt-12 rounded-2xl glass p-6 text-center text-sm text-[#c7c1cf]">{error || "Checking invitation…"}</p>
        )}
      </div>
    </div>
  );
}
